import { useFormContext } from 'react-hook-form';
import { useEffect, useState } from 'react';
import {
  accountSearchColumns,
  hanThanhToanSearchColumns,
  MA_CHUNG_TU,
  nhanVienSearchColumns,
  QUERY_KEYS,
  supplierSearchColumns
} from '@/constants';
import {
  CurrencyInput,
  DocumentNumberField,
  AritoIcon,
  FormField,
  NumericInputV2,
  SearchField
} from '@/components/custom/erp';
import { AccountModel, HanThanhToan, KhachHang, NhaCungCap, NhanVien, QuyenChungTu } from '@/types/schemas';
import { useDetailSelection } from '@/hooks/use-detail-selection';
import { useHoaDonMuaDichVuSearchContext } from '../../context';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInfoTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

const topCheckboxClassName = 'flex h-8 items-center gap-2';
const topSelectClassName =
  'h-8 rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 text-[13px] font-semibold text-black outline-none focus:border-b-blue-600 disabled:opacity-100';
const topNumberInputClassName =
  'h-8 rounded-none border-0 border-b border-b-gray-200 bg-transparent px-2 text-right text-[13px] font-semibold text-black outline-none focus:border-b-blue-600 disabled:opacity-100';

export function BasicInfoTab({ formMode, formState: { state, actions } }: BasicInfoTabProps) {
  const isViewMode = formMode === 'view';
  const { setValue } = useFormContext();
  const handleAccountSelection = useDetailSelection<AccountModel>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    onSelect: actions.setTaiKhoan
  });
  const pc_tao_yn = state.pc_tao_yn;
  const ck_yn = state.ck_yn;
  const loai_ck = state.loai_ck;
  const { transactionOptions } = useHoaDonMuaDichVuSearchContext();

  useEffect(() => {
    if (state.ma_gd || transactionOptions.length === 0) return;

    actions.setMaGd(transactionOptions[0]?.value, transactionOptions[0]?.ma_gd);
  }, [actions, state.ma_gd, transactionOptions]);

  return (
    <div className='flex flex-col gap-3 p-4 pt-3'>
      <div className='w-full overflow-x-auto'>
        <div className='flex min-h-8 min-w-[960px] items-center gap-7 xl:min-w-0'>
          <Label className='w-40 shrink-0 text-[14px]'>Giao dịch</Label>
          <div className='flex h-8 items-center gap-5'>
            {transactionOptions.map(option => (
              <label key={option.value} className='flex h-8 items-center gap-2 whitespace-nowrap text-[14px]'>
                <input
                  type='radio'
                  name='ma_gd'
                  value={option.value}
                  checked={(state.ma_gd || transactionOptions[0]?.value) === option.value}
                  disabled={isViewMode}
                  onChange={() => {
                    setValue('ma_gd', option.value);
                    actions.setMaGd(option.value, option.ma_gd);
                  }}
                  className='h-4 w-4 accent-blue-500'
                />
                {option.label}
              </label>
            ))}
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='pc_tao_yn'
              id='pc_tao_yn'
              disabled={isViewMode}
              checked={pc_tao_yn}
              onCheckedChange={checked => {
                setValue('pc_tao_yn', !!checked);
                actions.setPtTaoYn(!!checked);
              }}
            />
            <Label htmlFor='pc_tao_yn' className={pc_tao_yn ? 'sr-only' : 'whitespace-nowrap text-[14px]'}>
              Chi tiền
            </Label>
            <select
              name='ma_httt'
              value={state.ma_httt}
              disabled={isViewMode || !pc_tao_yn}
              aria-hidden={!pc_tao_yn}
              tabIndex={pc_tao_yn ? 0 : -1}
              onChange={event => {
                setValue('ma_httt', event.target.value);
                actions.setMaHttt(event.target.value);
              }}
              className={`${topSelectClassName} w-[150px] ${pc_tao_yn ? '' : 'hidden'}`}
            >
              <option value='TMM'>Tiền mặt</option>
              <option value='CKM'>Chuyển khoản</option>
              <option value='KM'>Khác</option>
            </select>
          </div>

          <div className={topCheckboxClassName}>
            <Checkbox
              name='ck_yn'
              id='ck_yn'
              disabled={isViewMode}
              checked={ck_yn}
              onCheckedChange={checked => {
                setValue('ck_yn', !!checked);
                actions.setCkYn(!!checked);
              }}
            />
            <Label htmlFor='ck_yn' className={ck_yn ? 'sr-only' : 'whitespace-nowrap text-[14px]'}>
              Chiết khấu
            </Label>
            <select
              name='loai_ck'
              value={state.loai_ck}
              disabled={isViewMode || !ck_yn}
              aria-hidden={!ck_yn}
              tabIndex={ck_yn ? 0 : -1}
              onChange={event => {
                setValue('loai_ck', event.target.value);
                actions.setLoaiCk(event.target.value);
              }}
              className={`${topSelectClassName} w-[250px] ${ck_yn ? '' : 'hidden'}`}
            >
              <option value='1'>Tự nhập</option>
              <option value='2'>Giảm % theo hóa đơn</option>
              <option value='3'>Giảm tiền trên tổng hóa đơn</option>
            </select>
            <NumericInputV2
              name='ck_tl_nt'
              disabled={isViewMode || !ck_yn || String(loai_ck) === '1'}
              value={state.ck_tl_nt}
              aria-hidden={!ck_yn || String(loai_ck) === '1'}
              tabIndex={ck_yn && String(loai_ck) !== '1' ? 0 : -1}
              onValueChange={nextValue => {
                setValue('ck_tl_nt', nextValue);
                actions.setCkTlNt(nextValue);
              }}
              className={`${topNumberInputClassName} w-[150px] ${ck_yn && String(loai_ck) !== '1' ? '' : 'hidden'}`}
            />
          </div>
        </div>
      </div>

      {/* Customer Information Section */}
      <div className='grid grid-cols-12 gap-6'>
        {/* Left Column */}
        <div className='col-span-6 space-y-1'>
          <div className='flex items-center'>
            <Label className='w-[195px]'>Mã nhà cung cấp</Label>
            <SearchField<KhachHang>
              type='text'
              name='ma_kh'
              value={state.nhaCungCap?.customer_code}
              searchEndpoint={`/${QUERY_KEYS.NHA_CUNG_CAP}/`}
              searchColumns={supplierSearchColumns}
              onRowSelection={actions.setNhaCungCap}
              columnDisplay='customer_code'
              dialogTitle='Danh mục nhà cung cấp'
              className='w-48'
              disabled={formMode === 'view'}
            />

            <Label className='w-20'>Mã số thuế</Label>
            <FormField
              name='ma_so_thue'
              type='text'
              disabled={formMode === 'view'}
              placeholder='Nhập và tra cứu'
              value={state.nhaCungCap?.tax_code}
              className='w-48'
            />
            <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
              <AritoIcon icon={15} className='shrink-0' />
            </button>
            <button className='ml-2 flex items-center justify-center border border-gray-200 px-3 py-1.5'>
              <AritoIcon icon={888} />
            </button>
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Tên nhà cung cấp</Label>
            <div className='flex-1'>
              <FormField
                name='ten_kh'
                type='text'
                disabled={formMode === 'view'}
                value={state.nhaCungCap?.customer_name}
                placeholder='Nhập tên khách hàng/đơn vị'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Địa chỉ</Label>
            <div className='flex-1'>
              <FormField
                name='dia_chi'
                type='text'
                disabled={formMode === 'view'}
                labelClassName='w-48'
                value={state.nhaCungCap?.address}
                placeholder='Nhập địa chỉ khách hàng'
              />
            </div>
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Mã nhân viên</Label>
            <SearchField<NhanVien>
              type='text'
              name='ma_nvmh'
              value={state.nhanVien?.ma_nhan_vien || state.nhaCungCap?.sales_rep_data?.ma_nhan_vien}
              relatedFieldValue={state.nhanVien?.ho_ten_nhan_vien || state.nhaCungCap?.sales_rep_data?.ho_ten_nhan_vien}
              searchEndpoint={`/${QUERY_KEYS.NHAN_VIEN}/`}
              columnDisplay='ma_nhan_vien'
              displayRelatedField='ho_ten_nhan_vien'
              searchColumns={nhanVienSearchColumns}
              dialogTitle='Danh sách nhân viên bán hàng'
              className='w-100'
              onRowSelection={actions.setNhanVien}
              disabled={formMode === 'view'}
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Tài khoản có</Label>
            <SearchField<AccountModel>
              type='text'
              name='tk'
              value={state.taiKhoan?.code || state.nhaCungCap?.account_data?.code}
              relatedFieldValue={state.taiKhoan?.name || state.nhaCungCap?.account_data?.name}
              searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}/`}
              columnDisplay='code'
              displayRelatedField='name'
              searchColumns={accountSearchColumns}
              dialogTitle='Danh sách tài khoản'
              className='w-100'
              onRowSelection={handleAccountSelection}
              disabled={formMode === 'view'}
            />
          </div>

          <div className='flex items-center'>
            <Label className='w-[195px]'>Diễn giải</Label>
            <div className='flex-1'>
              <FormField name='dien_giai' type='text' disabled={formMode === 'view'} />
            </div>
          </div>
        </div>

        {/* Middle Column */}
        <div className='col-span-3 w-full space-y-1'>
          <div className='ml-4 flex items-center'>
            <FormField
              name='du_cn_thu'
              label='Dư công nợ'
              labelClassName='w-32'
              inputClassName='w-6 text-red-600'
              className='w-64'
              defaultValue={0}
              disabled={true}
            />
          </div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='ong_ba'
              label='Người giao hàng'
              labelClassName='w-32'
              inputClassName='w-6 text-red-600'
              disabled={formMode === 'view'}
              className='w-64'
              value={state.nhaCungCap?.contact_person}
              placeholder='Nhập tên người nhận'
            />
          </div>

          <div className='ml-4 flex items-center'>
            <FormField
              name='e_mail'
              label='Email'
              labelClassName='w-32'
              inputClassName='w-64'
              className='w-64'
              disabled={formMode === 'view'}
              value={state.nhaCungCap?.email}
              placeholder='Nhập email'
            />
          </div>

          <div className='ml-4 flex items-center'>
            <Label className='w-[132px]'>Hạn thanh toán</Label>
            <SearchField<HanThanhToan>
              type='text'
              name='ma_tt'
              value={state.hanThanhToan?.ma_tt || state.nhaCungCap?.payment_term_data?.ma_tt}
              searchEndpoint={`/${QUERY_KEYS.HAN_THANH_TOAN}/`}
              columnDisplay='ma_tt'
              searchColumns={hanThanhToanSearchColumns}
              dialogTitle='Danh mục hạn thanh toán'
              className='w-64'
              onRowSelection={actions.setHanThanhToan}
              placeholder='Hạn thanh toán'
              disabled={formMode === 'view'}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className='col-span-3 flex'>
          {/* Tab Content */}
          <div className='flex-1 pr-2'>
            {/* Tab 0 */}
            <div>
              <DocumentNumberField
                ma_ct={MA_CHUNG_TU.MUA_HANG.HOA_DON_MUA_DICH_VU}
                quyenChungTu={state.quyenChungTu}
                onQuyenChungTuChange={actions.setQuyenChungTu}
                soChungTu={state.soChungTu}
                onSoChungTuChange={actions.setSoChungTu}
                disabled={formMode === 'view'}
                classNameSearchField='flex-1'
                formMode={formMode}
              />

              <FormField
                label='Ngày chứng từ'
                name='ngay_ct'
                type='date'
                disabled={formMode === 'view'}
                labelClassName='w-32 shrink-0'
              />

              <div className='flex items-center'>
                <FormField
                  name='so_ct0'
                  label='Số hóa đơn'
                  labelClassName='w-32 shrink-0'
                  inputClassName='w-64'
                  className='w-[300px]'
                  disabled={formMode === 'view'}
                  placeholder='Nhập số hóa đơn'
                />
              </div>

              <div className='flex items-center'>
                <FormField
                  name='so_ct2'
                  label='Ký hiệu'
                  labelClassName='w-32 shrink-0'
                  inputClassName='w-64'
                  className='w-[300px]'
                  disabled={formMode === 'view'}
                  placeholder='Nhập ký hiệu'
                />
              </div>

              <FormField
                label='Ngày hóa đơn'
                name='ngay_ct0'
                type='date'
                disabled={formMode === 'view'}
                labelClassName='w-32 shrink-0'
                onValueChange={value => actions.setNgayCt(String(value))}
              />

              <div className='flex items-center'>
                <CurrencyInput formMode={formMode} />
              </div>

              <FormField
                name='status'
                type='select'
                disabled={formMode === 'view'}
                label='Trạng thái'
                labelClassName='w-32 shrink-0'
                inputClassName='w-full'
                options={[
                  { value: '0', label: 'Lập chứng từ' },
                  { value: '3', label: 'Chờ duyệt' },
                  { value: '5', label: 'Xuất hóa đơn' },
                  { value: '7', label: 'Bỏ duyệt đơn hàng' }
                ]}
                defaultValue={'5'}
              />
              <div className='mt-1 flex'>
                <div className='mb-4 h-2 w-32 shrink-0' />
                <FormField
                  label='Dữ liệu nhận được'
                  name='transfer_yn'
                  type='checkbox'
                  disabled={true}
                  labelClassName='w-32'
                  defaultValue={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
